import {memo} from 'react';

import styled from 'styled-components';

import {IMoneycardsRes, MoneyCard} from '~/shared/services/bffService/api/interfaces/responses';
import {headingXS, smallBold} from '~/shared/theme/typography';
import {getLocalizationService} from '~/shared/services/localisationService';
import PieButton from '~/shared/theme/pie/PieButton';
import {flipOnLTR, rotateOnLTR} from '~/shared/theme/utils';
import {trackEvent} from '~/shared/services/analytics';
 
import ActionButton from './ActionButton';
import TenbisCardIcon from './assets/tenbisCardIcon.svg?react';
import ChevronLeft from './assets/chevronLeft.svg?react';
import {NewTag} from './styled';

const Root = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.h2`
  ${headingXS}
  margin-bottom: 4px;
`;

const CaptionRoot = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const Text = styled.span`
  ${smallBold}
`;

const StyledChevronLeft = styled(ChevronLeft)`
  ${rotateOnLTR}
  ${flipOnLTR`
    margin-right: 4px;
  `}
  path {
    fill: ${({theme}) => theme.colors.surface};
  }
`;

const ActivateButton = styled(PieButton).attrs({
  variant: 'primaryMedium',
})`
  ${flipOnLTR`
    padding-left: 4px;
  `}
`;

type TenbisCardCaptionProps = {
  fourDigits: string;
  isNew?: boolean;
};

const TenbisCardCaption = ({fourDigits, isNew}: TenbisCardCaptionProps) => {
  const {t} = getLocalizationService();
  return (
    <CaptionRoot>
      <TenbisCardIcon />
      <Text>{t('tenbis_card_xxx', {fourDigits})}</Text>
      {isNew && (<NewTag>{t('new_card')}</NewTag>)}
    </CaptionRoot>
  );
};

type TenbisCardsListProps = {
  moneycards: IMoneycardsRes;
  onMoneycardClick?: (moneycard: MoneyCard) => void;
  onActivateMoneycardClick?: (id: string) => void;
  isMinLargeMobile: boolean;
};

const TenbisCardsList = ({
  moneycards,
  onMoneycardClick,
  onActivateMoneycardClick,
  isMinLargeMobile,
}: TenbisCardsListProps) => {
  const {t} = getLocalizationService();

  const activateCardHandler = (id: string) => (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (onActivateMoneycardClick) {
      onActivateMoneycardClick(id);
    }
  };

  const clickCardHandler = (moneycard: MoneyCard) => {
    trackEvent('mcChoose_click');
    if (onMoneycardClick) {
      onMoneycardClick(moneycard);
    }
  };

  return (
    <Root>
      <Title>{t('your_10bis_cards')}</Title>
      {moneycards && moneycards.map((moneycard, index) => (
        <ActionButton
          key={moneycard.id}
          caption={(
            <TenbisCardCaption fourDigits={moneycard.cardLastDigits} isNew={!moneycard.isActivated} />
          )}
          withBorderTop={index > 0}
          onClick={() => clickCardHandler(moneycard)}
          actionButton={!moneycard.isActivated && isMinLargeMobile ? (
            <ActivateButton onClick={activateCardHandler(moneycard.id)}>
              {t('activate_card2')}
              <StyledChevronLeft />
            </ActivateButton>
          ) : null}
        />
      ))}
    </Root>
  );
};

export default memo(TenbisCardsList);
